import React, {useEffect, useState} from 'react'
import {Dispatch} from 'redux'
import {Loader} from '../../../services/Loader'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {AuthFooter, handleNotificationWithToastify, redirectToLoginPaymentIfRequired, resolveProductionLoginCompanyGate, isCompanyGracePeriodExpired, isCompanyDisabled, isWorkspaceDisabled, ALL_COMPANIES_INACTIVE_MESSAGE, sortInspectionCompaniesWithDecentFirst} from '../../../services/Functional'
import {Link, useNavigate} from 'react-router-dom'
import {AuthSidePanel} from './AuthSidePanel'
import {
  checkSubscriptionExpiry,
  getInspectionCompanies,
  getUserAccess,
  handleExpiredCompany,
  handleIsExpiredCompanyAPIcalling,
  handleSetWorkspaceAuth,
  hydrateWorkspaceAuth,
  selectWorkspace,
} from '../../../../setup/redux/auth/actions'
import {setActiveCompany} from '../../../../setup/redux/auth/slices'
import {resetBillingDetails} from '../../../../setup/redux/common/actions'
import './company-selection.scss'
import {endCall} from '../../../../setup/redux/common/slices'

export function CompanySelect() {
  const {config, workspaces, pickerToken, user, inspectionCompanies, accessToken}: any = useSelector<RootState>(
    (state) => ({
      user: state.auth.user,
      config: state.configuration.configurations,
      workspaces: state.auth.workspaces,
      pickerToken: state.auth.picker_token,
      inspectionCompanies: state.auth.inspection_companies,
      accessToken: state.auth.access_token,
    }),
    shallowEqual
  )

  const dispatch: Dispatch<any> = useDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  /** Redux clears workspaces in setWorkspaceAuth while this screen is still mounted; keep a snapshot so the list does not vanish under the loader. */
  const [workspaceListSnapshot, setWorkspaceListSnapshot] = useState<any[] | null>(null)
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null)

  const isSuperAdmin = user?.is_root === true

  useEffect(() => {
    dispatch(handleIsExpiredCompanyAPIcalling(false))
    dispatch(handleExpiredCompany(null))
  }, [])

  useEffect(() => {
    if (isSuperAdmin) {
      if (!inspectionCompanies || inspectionCompanies.length === 0) {
        navigate('/auth/login')
      }
    } else {
      // Skip redirect if workspace was just selected (access_token now set, picker_token cleared by setWorkspaceAuth)
      if (!accessToken && (!pickerToken || !workspaces || workspaces.length === 0)) {
        navigate('/auth/login')
      }
    }
  }, [pickerToken, workspaces, inspectionCompanies, isSuperAdmin, accessToken])

  const handleCompanyChange = (item: any) => {
    if (!isSuperAdmin && isWorkspaceDisabled(item)) {
      return
    }
    setSelectedWorkspace(item)
  }

  const isDisplayItemDisabled = (item: any) =>
    isSuperAdmin ? isCompanyDisabled(item.raw) : isWorkspaceDisabled(item.raw)

  const handleRedirectionPage = async () => {
    if (!selectedWorkspace) return

    if (isSuperAdmin) {
      dispatch(setActiveCompany(selectedWorkspace))
      setTimeout(() => {
        window.location.href = '/'
      }, 100)
      return
    }

    if (!pickerToken) return
    if (isWorkspaceDisabled(selectedWorkspace)) {
      return
    }
    setWorkspaceListSnapshot([...(workspaces || [])])
    setLoading(true)
    const response: any = await dispatch(
      selectWorkspace(selectedWorkspace.membership_id, pickerToken)
    )

    if (redirectToLoginPaymentIfRequired(response?.data)) {
      setLoading(false)
      setWorkspaceListSnapshot(null)
      dispatch(endCall({}))
      return
    }

    if (!response?.data?.success || !response?.data?.data) {
      setLoading(false)
      setWorkspaceListSnapshot(null)
      dispatch(endCall({}))
      handleNotificationWithToastify({
        success: false,
        message: response?.data?.message || 'Failed to select workspace',
      })
      return
    }

    const hydrated = hydrateWorkspaceAuth(response.data.data, user)
    dispatch(handleSetWorkspaceAuth(hydrated))
    const accessResult: any = await dispatch(getUserAccess())
    if (!accessResult?.success) {
      setLoading(false)
      setWorkspaceListSnapshot(null)
      dispatch(endCall({}))
      handleNotificationWithToastify({
        success: false,
        message: accessResult?.message || 'Unable to load permissions after selecting a company.',
      })
      return
    }

    const companiesResp: any = await dispatch(
      getInspectionCompanies(hydrated.active_inspection_company?.id)
    )
    const allCompanies = companiesResp?.data?.data?.inspection_companies
    const activeCompany =
      allCompanies?.find((c: any) => c.id === hydrated.active_inspection_company?.id) ||
      hydrated.active_inspection_company

    if (redirectToLoginPaymentIfRequired(null, activeCompany)) {
      setLoading(false)
      setWorkspaceListSnapshot(null)
      dispatch(endCall({}))
      return
    }

    const plan = activeCompany?.plans?.[0]
    const subscription = plan?.subscription
    const graceExpiredSubscription =
      isCompanyGracePeriodExpired(activeCompany) &&
      ((subscription?.type === 'paid' && activeCompany?.is_expired) ||
        (subscription?.type === 'free' && activeCompany?.is_expired))

    let expiryData = null
    if (graceExpiredSubscription && activeCompany?.id) {
      const expiryResp: any = await dispatch(checkSubscriptionExpiry(activeCompany.id))
      expiryData = expiryResp?.data
    }

    const gate = resolveProductionLoginCompanyGate(activeCompany, {
      requireCompanyAdmin: true,
      graceRequiresExpiredSubscription: true,
      expiryData,
    })

    if (gate.action === 'not_company_admin') {
      setLoading(false)
      setWorkspaceListSnapshot(null)
      dispatch(endCall({}))
      navigate('/error/auth')
      return
    }
    if (gate.action === 'redirect') {
      if (gate.resetBilling) {
        dispatch(resetBillingDetails())
      }
      setLoading(false)
      setWorkspaceListSnapshot(null)
      dispatch(endCall({}))
      window.location.href = gate.url
      return
    }
    if (gate.action === 'access_denied') {
      setLoading(false)
      setWorkspaceListSnapshot(null)
      dispatch(endCall({}))
      dispatch(handleIsExpiredCompanyAPIcalling(true))
      dispatch(handleExpiredCompany(gate.company))
      navigate('/error/access-denied')
      return
    }

    dispatch(endCall({}))
    setLoading(false)
    setWorkspaceListSnapshot(null)
    navigate('/dashboard')
  }

  // Super admin sees all companies; regular user sees their workspaces
  const workspacesForDisplay =
    !isSuperAdmin && loading && workspaceListSnapshot && workspaceListSnapshot.length > 0
      ? workspaceListSnapshot
      : workspaces || []
  const displayItems = isSuperAdmin
    ? sortInspectionCompaniesWithDecentFirst(inspectionCompanies || []).map((c: any) => ({
        key: c.id,
        label: c.name,
        raw: c,
      }))
    : workspacesForDisplay.map((ws: any) => ({
        key: ws.membership_id,
        label: ws.company_name,
        raw: ws,
      }))

  const selectedKey = isSuperAdmin ? selectedWorkspace?.id : selectedWorkspace?.membership_id
  const isSelectedDisabled =
    selectedWorkspace && !isSuperAdmin ? isWorkspaceDisabled(selectedWorkspace) : false
  const allCompaniesInactive =
    !isSuperAdmin &&
    displayItems.length > 0 &&
    displayItems.every((item: any) => isDisplayItemDisabled(item))

  return (
    <>
      {/* Same pattern as Login: fixed overlay keeps the workspace UI mounted underneath */}
      {loading && <Loader />}
      <div className='row w-100 h-100 p-0 m-0'>
        <div className='col-md-7 p-0 bg-white position-relative'>
          <div className='py-5 d-flex justify-content-between container align-items-center mb-8'>
            <img src={config?.actual_logo} alt='logo' width={90} />
            <Link
              to='https://decenthands.com/contact'
              className='link-primary fs-6 float-end'
              target='_blank'
            >
              Contact Us
            </Link>
          </div>
          <div className='company_select_form_container company_select_container mx-auto d-flex justify-content-center align-items-center h-100'>
            <div className='h-100 d-flex flex-column justify-content-center flex-gap-24 w-100'>
              <div className='d-flex flex-column gap-1'>
                <div className='font-size-20 font-weight-SemiBold letter-spacing-0-2 line-height-30 text-grayish-blue'>
                  Choose a Company
                </div>
                <div className='font-size-14 font-weight-regular line-height-22 text-charcoal com-select-subtitle d-flex flex-column'>
                  <span>Looks like you're logging in</span>
                  <span>Choose a company to start with</span>
                </div>
              </div>
              {allCompaniesInactive && (
                <div
                  className='font-size-14 font-weight-regular line-height-22 text-danger'
                  role='alert'
                >
                  {ALL_COMPANIES_INACTIVE_MESSAGE}
                </div>
              )}
              <div className='company-selection'>
                {displayItems.map((item: any) => {
                  const showDisabledStyle = isDisplayItemDisabled(item)
                  const disabled = !isSuperAdmin && showDisabledStyle
                  return (
                  <div className='' key={item.key}>
                    <label
                      className={`btn com-name-container d-flex align-items-center ${
                        !showDisabledStyle && selectedKey === item.key ? 'active-com-name-container' : ''
                      } ${showDisabledStyle ? 'disabled-com-name-container' : ''}`}
                      htmlFor={item.key}
                      onClick={() => !loading && !disabled && handleCompanyChange(item.raw)}
                      tabIndex={loading || disabled ? -1 : 0}
                      aria-disabled={disabled}
                    >
                      {item.label}
                    </label>
                  </div>
                  )
                })}
              </div>
              <button
                type='submit'
                disabled={!selectedWorkspace || loading || isSelectedDisabled || allCompaniesInactive}
                onClick={handleRedirectionPage}
                className={`btn btn-primary btn-pill w-100 ${!selectedWorkspace || isSelectedDisabled || allCompaniesInactive ? 'disabled-btn' : ''}`}
              >
                Let's go
                <img
                  className='ms-3'
                  src={'/media/icons/duotune/general/Arrow - Right.svg'}
                  alt='->'
                />
              </button>
            </div>
            <AuthFooter />
          </div>
        </div>
        <div className='col-md-5 p-0 background_card overflow-hidden position-relative'>
          <AuthSidePanel />
        </div>
      </div>
    </>
  )
}
